import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  analizarExcepciones,
  generarEscritoExcepciones,
  recalcularExcepciones,
} from 'services/escritosJudicialesService';
import SubidaDocumentos from './SubidaDocumentos';

const COLOR_VIABILIDAD = {
  alta: 'success',
  media: 'warning',
  baja: 'light',
  indeterminada: 'warning',
  // Neutro y no rojo: ya viven bajo el título "Descartadas", el rojo solo agrega ruido.
  no_aplica: 'light',
};

// `light` es el color de badge dominante en la app (6 usos) y el que corresponde a un contador.
const COLOR_CONTADOR = 'light';

const CLAVE_VIABILIDAD = {
  alta: 'cw_exc_viab_alta',
  media: 'cw_exc_viab_media',
  baja: 'cw_exc_viab_baja',
  indeterminada: 'cw_exc_viab_indeterminada',
  no_aplica: 'cw_exc_viab_noAplica',
};

// El código solo no le dice nada al abogado: "N°7-nulidad-derivada" es "Falta fuerza ejecutiva".
const CLAVE_NOMBRE = {
  'N°1': 'cw_exc_nom_n1',
  'N°4': 'cw_exc_nom_n4',
  'N°14': 'cw_exc_nom_n14',
  'N°14-exceso-mandato': 'cw_exc_nom_n14ExcesoMandato',
  'N°7-iliquidez': 'cw_exc_nom_n7Iliquidez',
  'N°7-personeria': 'cw_exc_nom_n7Personeria',
  'N°7-notarial': 'cw_exc_nom_n7Notarial',
  'N°7-nulidad-derivada': 'cw_exc_nom_n7NulidadDerivada',
  'N°7-exceso-mandato': 'cw_exc_nom_n7ExcesoMandato',
};

const CLAVE_TITULO = {
  pagare: 'cw_exc_tit_pagare',
  mutuo_hipotecario: 'cw_exc_tit_mutuo_hipotecario',
  otro: 'cw_exc_tit_otro',
  indeterminado: 'cw_exc_tit_indeterminado',
};

// El modelo devuelve el monto como cadena cruda: "3301052" en pantalla no se lee como plata.
const enPesos = (valor) => {
  const numero = Number(String(valor ?? '').replace(/[^\d]/g, ''));
  return valor && numero ? `$${numero.toLocaleString('es-CL')}` : valor;
};

/**
 * Excepciones a la ejecución (art. 464 CPC). El flujo de mayor volumen: ~386 al mes.
 *
 * El abogado corrige los dos HECHOS de cada excepción —si el presupuesto consta y en qué se apoya—
 * y el veredicto se recalcula en el backend. La viabilidad no se edita: es derivada.
 */
const ExcepcionesPanel = ({ DesignSystemComponents, caseData, t }) => {
  const { Badge, Button, Loader, Alert, Checkbox, Input } = DesignSystemComponents || {};

  const [documentos, setDocumentos] = useState([]);
  // Arranca DESMARCADO a propósito: marcarlo por defecto haría que el análisis afirme ante el
  // tribunal que la contraria no acompañó documentos, cuando puede ser que el abogado no los subió.
  const [ausenciasSonOmision, setAusenciasSonOmision] = useState(false);
  const [analisis, setAnalisis] = useState(null);
  const [analizados, setAnalizados] = useState([]);
  const [cargando, setCargando] = useState(false);
  const [recalculando, setRecalculando] = useState(false);
  const [verFallos, setVerFallos] = useState(false);
  const [error, setError] = useState(null);

  // Qué excepciones van al escrito. Se preseleccionan las de viabilidad alta, igual que el
  // sistema externo, y el abogado saca o agrega.
  const [seleccionadas, setSeleccionadas] = useState([]);

  // Lo que el código no puede deducir del expediente de la contraria, y de lo que dependen la
  // plantilla del escrito y su persona gramatical.
  const [comparecencia, setComparecencia] = useState({
    deudorEsPersonaJuridica: false,
    comparezcoConMandatoJudicial: false,
    deudorEsFemenino: false,
    hayAval: false,
    razonSocial: '',
    representanteLegal: '',
    rutRepresentante: '',
    documentoPersoneria: '',
    aval: '',
    rutAval: '',
  });

  const [escrito, setEscrito] = useState(null);
  const [generando, setGenerando] = useState(false);

  const abortAnalisis = useRef(null);
  const abortRecalculo = useRef(null);
  const abortGenerar = useRef(null);

  useEffect(
    () => () => {
      abortAnalisis.current?.abort();
      abortRecalculo.current?.abort();
      abortGenerar.current?.abort();
    },
    []
  );

  const analizar = useCallback(async () => {
    if (!documentos.length) return;
    abortAnalisis.current?.abort();
    const controller = new AbortController();
    abortAnalisis.current = controller;

    setCargando(true);
    setError(null);
    try {
      const data = await analizarExcepciones(
        documentos,
        caseData?.id,
        controller.signal,
        ausenciasSonOmision
      );
      setAnalisis(data);
      setAnalizados(documentos.map((d) => ({ nombre: d.name, bytes: d.size })));
      setEscrito(null);
      setSeleccionadas(
        (data.excepciones || [])
          .filter((e) => e.viabilidad === 'alta')
          .map((e) => e.codigo)
      );
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err.customMessage || err.response?.data?.message || err.message);
    } finally {
      setCargando(false);
    }
  }, [documentos, caseData?.id, ausenciasSonOmision]);

  const alternar = useCallback((codigo) => {
    setSeleccionadas((previo) =>
      previo.includes(codigo)
        ? previo.filter((c) => c !== codigo)
        : [...previo, codigo]
    );
  }, []);

  const descargar = useCallback((documento) => {
    if (!documento?.archivoBase64) return;
    // atob devuelve una cadena de bytes: hay que pasarla a Uint8Array o el .docx llega corrupto.
    const bytes = Uint8Array.from(atob(documento.archivoBase64), (c) => c.charCodeAt(0));
    const blob = new Blob([bytes], {
      type: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    });

    const url = URL.createObjectURL(blob);
    const enlace = document.createElement('a');
    enlace.href = url;
    enlace.download = documento.nombreDeArchivo || 'excepciones.docx';
    document.body.appendChild(enlace);
    enlace.click();
    document.body.removeChild(enlace);
    URL.revokeObjectURL(url);
  }, []);

  const generar = useCallback(async () => {
    if (!analisis || seleccionadas.length === 0) return;
    abortGenerar.current?.abort();
    const controller = new AbortController();
    abortGenerar.current = controller;

    setGenerando(true);
    setError(null);
    try {
      const data = await generarEscritoExcepciones(
        analisis,
        seleccionadas,
        comparecencia,
        caseData?.id,
        controller.signal
      );
      setEscrito(data);
      // Con marcadores sin resolver no se baja solo: el escrito tiene huecos y va a un tribunal.
      if (!data.marcadoresSinResolver?.length) descargar(data);
    } catch (err) {
      if (err.name === 'AbortError' || err.name === 'CanceledError') return;
      setError(err.customMessage || err.response?.data?.message || err.message);
    } finally {
      setGenerando(false);
    }
  }, [analisis, seleccionadas, comparecencia, caseData?.id, descargar]);

  const corregir = useCallback((indice, campo, valor) => {
    setAnalisis((previo) => {
      if (!previo) return previo;
      const siguiente = structuredClone(previo);
      siguiente.excepciones[indice][campo] = valor;

      abortRecalculo.current?.abort();
      const controller = new AbortController();
      abortRecalculo.current = controller;
      setRecalculando(true);

      recalcularExcepciones(siguiente, controller.signal)
        .then((data) => setAnalisis(data))
        .catch((err) => {
          if (err.name === 'AbortError' || err.name === 'CanceledError') return;
          setError(err.customMessage || err.message);
        })
        .finally(() => setRecalculando(false));

      return siguiente;
    });
  }, []);

  // El índice es el del array completo: `corregir` escribe por posición en el análisis.
  const conIndice = useMemo(
    () => (analisis?.excepciones || []).map((excepcion, indice) => ({ excepcion, indice })),
    [analisis]
  );

  const oponibles = useMemo(
    () => conIndice.filter((e) => e.excepcion.viabilidad !== 'no_aplica'),
    [conIndice]
  );

  const descartadas = useMemo(
    () => conIndice.filter((e) => e.excepcion.viabilidad === 'no_aplica'),
    [conIndice]
  );

  const nombreDocumento = useCallback((tipo) => t(`cw_exc_doc_${tipo}`), [t]);

  /* El único dato que el código NO puede deducir: un documento ausente puede ser omisión de la
     contraria —y funda la excepción— o un archivo que el abogado no alcanzó a subir. */
  const declaracionAusencias = Checkbox ? (
    <div>
      <Checkbox
        id='excepciones-ausencias-son-omision'
        label={t('cw_exc_ausenciasOmision')}
        checked={ausenciasSonOmision}
        onChange={(e) => setAusenciasSonOmision(e.target.checked)}
      />
      <p className='caption color-gray-600 mb-0 mt-1 prosa'>
        {t('cw_exc_ausenciasOmision_hint')} <b>{t('cw_exc_ausenciasOmision_warn')}</b>.
      </p>
    </div>
  ) : null;

  const seccion = (titulo, contenido, extra = null) => (
    <section className='mb-4'>
      <div className='d-flex align-items-center gap-2 mb-2'>
        <p className='font-light mb-0'>{titulo}</p>
        {extra}
      </div>
      {contenido}
    </section>
  );

  const dato = (clave, valor) =>
    valor ? (
      <div className='col-6 col-md-3'>
        <div className='caption color-gray-600'>{t(clave)}</div>
        <div className='fw-semibold'>{valor}</div>
      </div>
    ) : null;

  const tarjeta = ({ excepcion, indice }, editable) => (
    <div
      key={`${excepcion.codigo}-${indice}`}
      className={`tarjeta-excepcion mb-3 ${editable ? '' : 'descartada'}`}
    >
      <div className='d-flex align-items-center flex-wrap gap-2 mb-2'>
        {Checkbox && editable && (
          <Checkbox
            id={`excepcion-${excepcion.codigo}-${indice}`}
            label=''
            checked={seleccionadas.includes(excepcion.codigo)}
            onChange={() => alternar(excepcion.codigo)}
          />
        )}
        <span className='fw-semibold'>{excepcion.codigo}</span>
        {CLAVE_NOMBRE[excepcion.codigo] && (
          <span>{t(CLAVE_NOMBRE[excepcion.codigo])}</span>
        )}
        <Badge
          text={
            CLAVE_VIABILIDAD[excepcion.viabilidad]
              ? t(CLAVE_VIABILIDAD[excepcion.viabilidad])
              : excepcion.viabilidad
          }
          color={COLOR_VIABILIDAD[excepcion.viabilidad] || 'secondary'}
          size='sm'
        />
      </div>

      <p className='prosa mb-2'>{excepcion.fundamentoBreve}</p>

      {excepcion.citaTextual && (
        <blockquote className='prosa caption color-gray-600 border-start ps-3 mb-2'>
          {excepcion.citaTextual}
        </blockquote>
      )}

      {excepcion.documentosFaltantes?.length > 0 && (
        <p className='caption color-gray-600 mb-2'>
          {t('cw_exc_faltaEvaluar', {
            lista: excepcion.documentosFaltantes.map(nombreDocumento).join(', '),
          })}
        </p>
      )}

      {/* Solo esto es un aviso de verdad: una cita que no consta se vuelve en contra en estrados. */}
      {excepcion.observacionEvidencia && (
        <Alert className='alerta-en-linea' variant='warning'>{excepcion.observacionEvidencia}</Alert>
      )}

      {editable && (
        <div className='row g-3 mt-1'>
          <div className='col-12 col-md-5'>
            <div className='caption color-gray-600 mb-1'>{t('cw_exc_presupuesto')}</div>
            <select
              className='form-select'
              value={excepcion.presupuestoConsta || 'si'}
              onChange={(e) => corregir(indice, 'presupuestoConsta', e.target.value)}
            >
              <option value='si'>{t('cw_exc_presupuesto_si')}</option>
              <option value='no'>{t('cw_exc_presupuesto_no')}</option>
            </select>
          </div>

          <div className='col-12 col-md-5'>
            <div className='caption color-gray-600 mb-1'>{t('cw_exc_solidez')}</div>
            <select
              className='form-select'
              value={excepcion.solidez || 'interpretacion_discutible'}
              onChange={(e) => corregir(indice, 'solidez', e.target.value)}
            >
              <option value='jurisprudencia_o_texto_expreso'>{t('cw_exc_solidez_texto')}</option>
              <option value='interpretacion_discutible'>{t('cw_exc_solidez_discutible')}</option>
              <option value='solo_formal'>{t('cw_exc_solidez_formal')}</option>
            </select>
          </div>
        </div>
      )}
    </div>
  );

  const faltantes = analisis?.documentosDelExpedienteFaltantes || [];

  return (
    <div>
      <SubidaDocumentos
        DesignSystemComponents={DesignSystemComponents}
        titulo={t('cw_exc_adjunta')}
        ayuda={t('cw_exc_adjunta_hint')}
        archivos={documentos}
        onArchivos={setDocumentos}
        onAnalizar={analizar}
        cargando={cargando}
        error={error}
        etiquetaBoton={t('cw_analyze')}
        etiquetaCargando={t('cw_analyzing')}
        antesDelBoton={declaracionAusencias}
        t={t}
      />

      {cargando && <Loader />}

      {analisis && (
        <>
          <hr className='line mt-4 mb-4' />

          {analisis.jurisprudencia?.length > 0 && (
            <section className='mb-4'>
              <button
                type='button'
                className='desplegable d-flex align-items-center gap-2 mb-1'
                aria-expanded={verFallos}
                onClick={() => setVerFallos((v) => !v)}
              >
                <span className='caret color-gray-600'>›</span>
                <span className='font-light'>{t('cw_exc_jurisprudencia')}</span>
                <Badge text={String(analisis.jurisprudencia.length)} color={COLOR_CONTADOR} size='sm' />
                <span className='caption link'>{t('cw_exc_jurisprudencia_ver')}</span>
              </button>
              <p className='caption color-gray-600 prosa mb-0'>
                {t('cw_exc_jurisprudencia_hint')}
              </p>

              {verFallos && (
                <ul className='list-auto mt-2 mb-0'>
                  {analisis.jurisprudencia.map((f) => (
                    <li key={f.rol} className='caption mb-1'>
                      <span className='fw-semibold'>{f.rol}</span>
                      {f.tribunal && <span className='color-gray-600'> · {f.tribunal}</span>}
                      {f.excepcion && <span className='color-gray-600'> · {f.excepcion}</span>}
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}

          <hr className='line mb-4' />

          {seccion(
            t('cw_exc_datosCaso'),
            <div className='row g-3'>
              {dato('cw_exc_tribunal', analisis.causa?.tribunal)}
              {dato('cw_exc_rol', analisis.causa?.rol)}
              {dato('cw_exc_acreedor', analisis.causa?.ejecutante)}
              {dato('cw_exc_deudor', analisis.causa?.ejecutado)}
              {dato(
                'cw_exc_titulo',
                CLAVE_TITULO[analisis.causa?.tipoTitulo]
                  ? t(CLAVE_TITULO[analisis.causa.tipoTitulo])
                  : analisis.causa?.tipoTitulo
              )}
              {dato('cw_exc_montoTitulo', enPesos(analisis.datos?.montoCapital))}
            </div>
          )}

          <hr className='line mb-4' />

          {/* Antes del análisis, como en el sistema externo: define la plantilla y la persona
              gramatical de todo el escrito, así que el abogado lo decide primero. */}
          <section className='mb-4'>
            <p className='font-light mb-1'>{t('cw_exc_comparecencia')}</p>
            <p className='caption color-gray-600 prosa'>{t('cw_exc_comparecencia_hint')}</p>

            {Checkbox && (
              <div className='row g-2 mb-3'>
                <div className='col-12 col-md-6 col-xl-3'>
                  <Checkbox
                    id='comparecencia-persona-juridica'
                    label={t('cw_exc_esEmpresa')}
                    checked={comparecencia.deudorEsPersonaJuridica}
                    onChange={(e) =>
                      setComparecencia((p) => ({
                        ...p,
                        deudorEsPersonaJuridica: e.target.checked,
                      }))
                    }
                  />
                </div>
                <div className='col-12 col-md-6 col-xl-3'>
                  <Checkbox
                    id='comparecencia-mandato-judicial'
                    label={t('cw_exc_mandato')}
                    checked={comparecencia.comparezcoConMandatoJudicial}
                    onChange={(e) =>
                      setComparecencia((p) => ({
                        ...p,
                        comparezcoConMandatoJudicial: e.target.checked,
                      }))
                    }
                  />
                </div>
                <div className='col-12 col-md-6 col-xl-3'>
                  <Checkbox
                    id='comparecencia-genero'
                    label={t('cw_exc_femenino')}
                    checked={comparecencia.deudorEsFemenino}
                    onChange={(e) =>
                      setComparecencia((p) => ({ ...p, deudorEsFemenino: e.target.checked }))
                    }
                  />
                </div>
                <div className='col-12 col-md-6 col-xl-3'>
                  <Checkbox
                    id='comparecencia-aval'
                    label={t('cw_exc_hayAval')}
                    checked={comparecencia.hayAval}
                    onChange={(e) =>
                      setComparecencia((p) => ({ ...p, hayAval: e.target.checked }))
                    }
                  />
                </div>
              </div>
            )}

            {/* El caratulado trae la razón social, pero no quién representa ni con qué documento:
                sin esto el escrito sale con el marcador de personería sin resolver. */}
            {Input && comparecencia.deudorEsPersonaJuridica && (
              <div className='row g-3 mb-3'>
                <div className='col-12 col-md-6 col-xl-3'>
                  <Input
                    label={t('cw_exc_razonSocial')}
                    value={comparecencia.razonSocial}
                    onChange={(e) =>
                      setComparecencia((p) => ({ ...p, razonSocial: e.target.value }))
                    }
                  />
                </div>
                <div className='col-12 col-md-6 col-xl-3'>
                  <Input
                    label={t('cw_exc_representante')}
                    value={comparecencia.representanteLegal}
                    onChange={(e) =>
                      setComparecencia((p) => ({ ...p, representanteLegal: e.target.value }))
                    }
                  />
                </div>
                <div className='col-12 col-md-6 col-xl-3'>
                  <Input
                    label={t('cw_exc_rutRepresentante')}
                    placeholder={t('cw_exc_rutEjemplo')}
                    value={comparecencia.rutRepresentante}
                    onChange={(e) =>
                      setComparecencia((p) => ({ ...p, rutRepresentante: e.target.value }))
                    }
                  />
                </div>
                <div className='col-12 col-md-6 col-xl-3'>
                  <Input
                    label={t('cw_exc_personeria')}
                    placeholder={t('cw_exc_personeriaEjemplo')}
                    value={comparecencia.documentoPersoneria}
                    onChange={(e) =>
                      setComparecencia((p) => ({ ...p, documentoPersoneria: e.target.value }))
                    }
                  />
                </div>
              </div>
            )}

            {Input && comparecencia.hayAval && (
              <div className='row g-3 mb-3'>
                <div className='col-12 col-md-6 col-xl-3'>
                  <Input
                    label={t('cw_exc_aval')}
                    placeholder={t('cw_exc_nombreCompleto')}
                    value={comparecencia.aval}
                    onChange={(e) => setComparecencia((p) => ({ ...p, aval: e.target.value }))}
                  />
                </div>
                <div className='col-12 col-md-6 col-xl-3'>
                  <Input
                    label={t('cw_exc_rutAval')}
                    placeholder={t('cw_exc_rutEjemplo')}
                    value={comparecencia.rutAval}
                    onChange={(e) => setComparecencia((p) => ({ ...p, rutAval: e.target.value }))}
                  />
                </div>
              </div>
            )}
          </section>

          <hr className='line mb-4' />

          {/* Lo que la contraria NO acompañó va primero: es lo que condiciona todo lo que sigue. */}
          {faltantes.length > 0 && (
            <section className='mb-4'>
              <Alert className='alerta-en-linea' variant='warning'>
                <div className='fw-semibold'>
                  {analisis.ausenciasSonOmisionDelEjecutante
                    ? t('cw_exc_omitidos')
                    : t('cw_exc_faltanObligatorios')}
                </div>
                <ul className='list-auto mb-2 mt-1'>
                  {faltantes.map((tipo) => (
                    <li key={tipo}>{nombreDocumento(tipo)}</li>
                  ))}
                </ul>
                <div className='caption prosa'>
                  {analisis.ausenciasSonOmisionDelEjecutante
                    ? t('cw_exc_omitidos_hint')
                    : t('cw_exc_faltanObligatorios_hint')}
                </div>
              </Alert>
            </section>
          )}

          {analizados.length > 0 &&
            seccion(
              t('cw_exc_documentos'),
              <ul className='list-auto mb-0'>
                {analizados.map((d) => (
                  <li key={d.nombre} className='caption color-gray-600'>
                    {d.nombre} · {(d.bytes / 1048576).toFixed(1)} MB
                  </li>
                ))}
              </ul>
            )}

          {analisis.notasDeLectura?.length > 0 &&
            seccion(
              t('cw_exc_ocr'),
              <ul className='list-auto mb-0 prosa'>
                {analisis.notasDeLectura.map((n, i) => (
                  <li key={i} className='caption color-gray-600 mb-1'>
                    {n}
                  </li>
                ))}
              </ul>
            )}

          <hr className='line mb-4' />

          <section className='mb-4'>
            <div className='d-flex align-items-center gap-2 mb-1'>
              <p className='font-light mb-0'>{t('cw_exc_aOponer')}</p>
              <Badge text={String(oponibles.length)} color={COLOR_CONTADOR} size='sm' />
              {recalculando && (
                <span className='caption color-gray-600'>{t('cw_recalculating')}</span>
              )}
            </div>
            <p className='caption color-gray-600 prosa'>{t('cw_exc_viabilidad_hint')}</p>

            {oponibles.length === 0 ? (
              <Alert className='alerta-en-linea' variant='warning'>
                {t('cw_exc_ningunaOponible')}
              </Alert>
            ) : (
              oponibles.map((e) => tarjeta(e, true))
            )}
          </section>

          {descartadas.length > 0 && (
            <section className='mb-4'>
              <div className='d-flex align-items-center gap-2 mb-2'>
                <p className='font-light mb-0'>{t('cw_exc_descartadas')}</p>
                <Badge text={String(descartadas.length)} color={COLOR_CONTADOR} size='sm' />
              </div>
              {descartadas.map((e) => tarjeta(e, false))}
            </section>
          )}

          {analisis.advertencias?.length > 0 && (
            <>
              <hr className='line mb-4' />
              {seccion(
                t('cw_warnings'),
                <div className='d-flex flex-column gap-2'>
                  {analisis.advertencias.map((a, i) => (
                    <Alert className='alerta-en-linea' key={i} variant='warning'>
                      {a}
                    </Alert>
                  ))}
                </div>
              )}
            </>
          )}

          {analisis.datosFaltantes?.length > 0 &&
            seccion(
              t('cw_missing'),
              <ul className='list-auto mb-0 prosa'>
                {analisis.datosFaltantes.map((d, i) => (
                  <li key={i} className='caption color-gray-600 mb-1'>
                    {d}
                  </li>
                ))}
              </ul>
            )}

          <hr className='line mb-4' />

          <section>
            <div className='d-flex align-items-center gap-3'>
              <Button
                label={
                  generando
                    ? t('cw_exc_redactando')
                    : t('cw_exc_generar', { count: seleccionadas.length })
                }
                size='sm'
                variant='primary'
                onClick={generar}
                disabled={seleccionadas.length === 0 || generando}
              />
              {escrito && (
                <Button
                  label={t('cw_writ_download')}
                  size='sm'
                  variant='naked'
                  onClick={() => descargar(escrito)}
                />
              )}
            </div>

            {escrito?.marcadoresSinResolver?.length > 0 && (
              <Alert className='alerta-en-linea mt-3' variant='danger'>
                {t('cw_exc_sinResolver')}: {escrito.marcadoresSinResolver.join(', ')}
              </Alert>
            )}

            {escrito?.advertencias?.length > 0 && (
              <div className='mt-3'>
                <p className='font-light mb-2'>{t('cw_writ_review')}</p>
                <div className='d-flex flex-column gap-2'>
                  {escrito.advertencias.map((a, i) => (
                    <Alert className='alerta-en-linea' key={i} variant='warning'>
                      {a}
                    </Alert>
                  ))}
                </div>
              </div>
            )}
          </section>
        </>
      )}
    </div>
  );
};

export default ExcepcionesPanel;
